import { FC } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { IconButton, styled } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import Text from '../Text';
import { PRIMARY, RED, STROKE, TEXT } from '../../Helpers/colors';
import {
	InputBackgroundType,
	backgroundColors,
	disabledBackgroundColors,
	errorBackgroundColors,
	focusBackgroundColors,
} from '../Input';
import { ChevronLeft, ChevronRight } from '../../Assets/svgIcons';
import { Calendar } from '../../Assets/sessionDetailsSVGs';

const Wrapper = styled('div')(() => ({
	'& .MuiFormControl-root': {
		width: '100%',
	},
}));

const CustomDateInput = styled(DatePicker)<DatePickerProperties>(({ error, disabled, backgroundType = 'primary' }) => ({
	'& .MuiInputBase-root': {
		display: 'flex',
		padding: '10px 12px',
		alignItems: 'center',
		gap: '8px',
		alignSelf: 'stretch',
		borderRadius: '9px',
		border: `1px solid ${error ? RED.BASE : STROKE.SECONDARY}`,
		backgroundColor: disabled ? backgroundColors[backgroundType] : disabledBackgroundColors[backgroundType],
		width: '100%',
		height: '40px',
		color: TEXT.PRIMARY,
		fontFamily: 'Inter',
		fontSize: '14px',
		fontWeight: 400,
		lineHeight: '20px',
	},
	'& .MuiOutlinedInput-notchedOutline': {
		border: 'none',
	},
	'& .Mui-focused .MuiOutlinedInput-notchedOutline': {
		border: error ? `1px solid ${RED.BASE}` : `1.6px solid ${PRIMARY.BASE}`,
		borderRadius: '9px',
	},
	'& .Mui-focused': {
		borderRadius: '9px',
		backgroundColor: error ? errorBackgroundColors[backgroundType] : focusBackgroundColors[backgroundType],
	},
	input: {
		padding: '0',
		':focus': {
			backgroundColor: 'transparent',
		},
		'::placeholder': {
			color: TEXT.QUATERNARY,
		},
	},
}));

interface DatePickerProperties {
	backgroundType?: InputBackgroundType;
	className?: string;
	description?: string;
	disableFuture?: boolean;
	disablePast?: boolean;
	disabled?: boolean;
	error?: boolean;
	errorMessage?: string;
	label?: string;
	onBlur?: () => void;
	onChange: (value: Dayjs | null) => void;
	value: Dayjs | null;
}

const CustomDate: FC<DatePickerProperties> = ({
	label = '',
	value,
	onChange,
	className = '',
	error = false,
	disabled = false,
	errorMessage = '',
	description = '',
	onBlur,
	disableFuture = false,
	disablePast = false,
	backgroundType = 'primary',
}) => (
	<Wrapper className={className} onBlur={onBlur}>
		<Text
			className={`inputLabel ${label ? 'active' : ''}`}
			title={label}
			type="psm"
			fontWeight="medium"
			color={disabled ? TEXT.QUATERNARY : TEXT.PRIMARY}
		/>
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<CustomDateInput
				value={value}
				format="MM/DD/YYYY"
				onChange={onChange}
				disabled={disabled}
				disableFuture={disableFuture}
				disablePast={disablePast}
				dayOfWeekFormatter={(day) => day.format('dd')}
				backgroundType={backgroundType}
				slots={{
					toolbar: (props) => {
						const currentDate = props.value ? dayjs(props.value) : dayjs();
						const formattedMonth = currentDate.format('MMMM');
						const formattedYear = currentDate.format('YYYY');

						return (
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									padding: '16px',
									borderBottom: `1px solid ${STROKE.SECONDARY}`,
								}}
							>
								<div onClick={() => props.onChange(currentDate.subtract(1, 'month'))}>
									<ChevronLeft />
								</div>
								<div
									style={{
										display: 'flex',
										flex: '1 0 0',
										justifyContent: 'center',
										gap: '4px',
									}}
								>
									<Text title={formattedMonth} type="h6" fontWeight="semiBold" />
									<Text title={formattedYear} type="h6" color={TEXT.TERTIARY} />
								</div>
								<div onClick={() => props.onChange(currentDate.add(1, 'month'))}>
									<ChevronRight />
								</div>
							</div>
						);
					},
					calendarHeader: () => null,
				}}
				slotProps={{
					textField: (params) => {
						const endAdornmentProperties = params.InputProps?.endAdornment?.props?.children?.props;

						return {
							...params,
							InputProps: {
								...params.InputProps,
								endAdornment: null,
								startAdornment: (
									<IconButton
										onClick={endAdornmentProperties?.onClick}
										edge="start"
										style={{ height: 'max-content', marginRight: 0 }}
									>
										<Calendar />
									</IconButton>
								),
							},
						};
					},
					popper: {
						sx: {
							'& .MuiPaper-root': {
								borderRadius: '16px',
								boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
								'>div': {
									display: 'block',
								},
							},
							'& .MuiDateCalendar-root': {
								height: 'max-content',
							},
							'& .MuiPickersDay-today': {
								border: `2px solid ${PRIMARY.LIGHT[48]} !important`,
								color: PRIMARY.LIGHT[16],
							},
						},
					},
					day: {
						sx: {
							borderRadius: '9px',
							'&.Mui-selected': {
								backgroundColor: PRIMARY.TERTIARY,
								color: TEXT.SECONDARY,
								borderRadius: '0',
								'&:hover': {
									borderRadius: '9px',
									backgroundColor: PRIMARY.TERTIARY,
								},
								'&:focus': {
									backgroundColor: PRIMARY.TERTIARY,
									color: TEXT.SECONDARY,
									borderRadius: '0',
								},
							},
							'&:hover': {
								backgroundColor: 'rgba(0, 0, 0, 0.04)',
							},
						},
					},
				}}
			/>
		</LocalizationProvider>
		<Text className={`error ${error ? 'active' : ''}`} title={errorMessage} fontWeight="medium" color={RED.BASE} />
		<Text
			className={`inputDescription ${!error && Boolean(description) ? 'active' : ''}`}
			title={description}
			fontWeight="medium"
		/>
	</Wrapper>
);

export default CustomDate;
